// src/components/aiTalentDrive/DriveHero.tsx
// Hero of /ai-talent-drive. The idea is the product's promise drawn literally:
// one voice (the interview) leaves the left as a single lime wave and fans out
// into 50+ hairlines, one per company. The cursor leans on the amplitude and
// scrolling the page splits the wave sooner. Plain canvas, no dependency.
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowUpRightIcon, LightningIcon } from "@phosphor-icons/react";
import { MagneticButton } from "./driveMotion";
import TalentPoolLive from "./TalentPoolLive";

// Hiring companies sign in (or sign up) on our own page: the live talent
// pool, then the work-email form.
const COMPANIES_PATH = "/ai-talent-drive/hiring-companies";

const scrollToId = (id: string) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const smooth = (v: number) => {
  const t = Math.min(1, Math.max(0, v));
  return t * t * (3 - 2 * t);
};

function SignalField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !host || !ctx) return;

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0;
    let h = 0;
    let raf = 0;
    let onScreen = true;
    const mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const r = host.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (ms: number) => {
      const t = ms / 1000;
      mouse.x += (mouse.tx - mouse.x) * 0.06;
      mouse.y += (mouse.ty - mouse.y) * 0.06;

      // 0 at the top of the page, 1 once the hero has scrolled away
      const rect = host.getBoundingClientRect();
      const scrolled = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));

      const small = w < 700;
      const lines = small ? 22 : 48;
      const steps = small ? 70 : 130;
      const x0 = 0;
      const x1 = w * 0.96;
      const cy = h * 0.5;
      const fan = h * (small ? 0.4 : 0.42);
      const amp = h * 0.075 * (0.8 + Math.abs(mouse.y - 0.5) * 2.4);
      const splitA = 0.3 - 0.2 * scrolled;
      const splitB = 0.78 - 0.1 * scrolled;

      const yAt = (p: number, k: number, i: number) => {
        const f = smooth((p - splitA) / (splitB - splitA));
        const voice = Math.sin(p * 14 - t * 2.2 + mouse.x * 3) * Math.sin(p * 5 + t * 0.7);
        const env = (1 - f * 0.65) * Math.sin(Math.min(1, p * 3.2) * Math.PI * 0.5);
        const ripple = Math.sin(p * 9 + i * 0.45 - t * 1.4) * h * 0.012 * f;
        return cy + k * fan * f + voice * amp * env + ripple;
      };

      ctx.clearRect(0, 0, w, h);
      ctx.lineJoin = "round";

      // the fan: every company's line, the accent ones in lime
      for (let i = 0; i < lines; i++) {
        const k = (i / (lines - 1)) * 2 - 1;
        const accent = i % 6 === 3;
        ctx.beginPath();
        for (let s = 0; s <= steps; s++) {
          const p = s / steps;
          const x = x0 + (x1 - x0) * p;
          const y = yAt(p, k, i);
          if (s) ctx.lineTo(x, y);
          else ctx.moveTo(x, y);
        }
        ctx.lineWidth = accent ? 1.4 : 1;
        ctx.strokeStyle = accent
          ? "rgba(208,249,97,.55)"
          : `rgba(255,255,255,${0.08 + 0.12 * (1 - Math.abs(k))})`;
        ctx.stroke();

        // the line ends in a node: a company
        ctx.beginPath();
        ctx.arc(x1, yAt(1, k, i), accent ? 3 : 2, 0, Math.PI * 2);
        ctx.fillStyle = accent ? "rgb(208,249,97)" : "rgba(255,255,255,.55)";
        ctx.fill();

        // a pulse of signal travelling down the accent lines
        if (accent) {
          const p = (t * 0.16 + i * 0.137) % 1;
          ctx.beginPath();
          ctx.arc(x0 + (x1 - x0) * p, yAt(p, k, i), 3.2, 0, Math.PI * 2);
          ctx.fillStyle = "rgb(208,249,97)";
          ctx.fill();
        }
      }

      // the voice: one thick lime wave, with a soft halo, until it splits
      const voiceLine = (width: number, alpha: number) => {
        ctx.beginPath();
        for (let s = 0; s <= steps; s++) {
          const p = s / steps;
          const x = x0 + (x1 - x0) * p;
          if (s) ctx.lineTo(x, yAt(p, 0, 0));
          else ctx.moveTo(x, yAt(p, 0, 0));
        }
        ctx.lineWidth = width;
        ctx.strokeStyle = `rgba(208,249,97,${alpha})`;
        ctx.stroke();
      };
      voiceLine(12, 0.1);
      voiceLine(3, 1);
    };

    const loop = (ms: number) => {
      raf = 0;
      if (!onScreen || document.hidden) return;
      draw(ms);
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (!raf && !still) raf = requestAnimationFrame(loop);
    };

    const onMove = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      mouse.tx = (e.clientX - r.left) / r.width;
      mouse.ty = (e.clientY - r.top) / r.height;
    };
    const onLeave = () => {
      mouse.tx = 0.5;
      mouse.ty = 0.5;
    };
    const onResize = () => {
      resize();
      if (still) draw(1200);
    };
    const onVisibility = () => start();

    resize();
    if (still) draw(1200);
    else start();

    const io = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen) start();
    });
    io.observe(host);
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <div className="td-hero__signal" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

type Props = {
  closesDay: string;
  closesTime: string;
  closed: boolean;
  /* Where /hiring-circuit/metrics/ lives, for the live pool on the right */
  apiBase: string;
};

// Phones get the live pool drawn small over the wave, between the title and
// the promise; wider screens keep it in the right column. One instance either
// way, so the metrics are fetched once.
const PHONE = "(max-width: 899px)";
const usePhone = () => {
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia(PHONE).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE);
    const update = () => setPhone(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return phone;
};

export default function DriveHero({ closesDay, closesTime, closed, apiBase }: Props) {
  const phone = usePhone();
  return (
    <section className="td-hero" id="td-hero" aria-labelledby="aitd-title">
      <div className="wrap td-hero__inner td-hero__cols">
       <div className="td-hero__copy">
        <div className="td-hero__head">
          <p className="td-hero__kicker">
            <span className="td-dot" aria-hidden="true" />
            Oct - Nov 2026 <span aria-hidden="true">&middot;</span> Nationwide virtual drive
          </p>
          <h1 className="td-hero__title" id="aitd-title">
            India&rsquo;s largest <span className="td-hero__lime">AI Talent Drive</span>
          </h1>
        </div>

        <div className="td-hero__wave">
          <SignalField />
          {phone && (
            <div className="td-hero__live td-hero__live--phone">
              <TalentPoolLive apiBase={apiBase} variant="bubbles" />
            </div>
          )}
        </div>

        <div className="td-hero__foot">
          <p className="td-hero__promise">
            <LightningIcon className="td-hero__bolt" weight="fill" aria-hidden="true" />
            <span className="td-hero__promise-a">One Interview</span>
            <span>
              Auto-apply to <b>50+ Companies</b> for AI-Jobs
            </span>
          </p>
          <div className="td-hero__cta">
            <MagneticButton className="td-btn td-btn--lime" onClick={() => scrollToId("register")}>
              <span>{closed ? "Registration closed" : "Register as a candidate"}</span>
              <ArrowRightIcon size={20} weight="bold" aria-hidden="true" />
            </MagneticButton>
            <Link className="td-btn td-btn--line" to={COMPANIES_PATH}>
              <span>Hiring Company? Login</span>
              <ArrowUpRightIcon size={20} weight="bold" aria-hidden="true" />
            </Link>
          </div>
          {!closed && (
            <p className="td-hero__closes">
              Registration closes <b>{closesDay}</b>, {closesTime} IST
            </p>
          )}
          <Link className="td-hero__pool" to={COMPANIES_PATH}>
            See the full talent pool &amp; sign in
            <ArrowRightIcon size={16} weight="bold" aria-hidden="true" />
          </Link>
        </div>
       </div>

        {/* the live pool floats over the wave's fan */}
        {!phone && (
          <div className="td-hero__live">
            <TalentPoolLive apiBase={apiBase} variant="bubbles" />
          </div>
        )}
      </div>

      <div className="td-hero__factsbar">
        <ul className="wrap td-hero__facts">
          <li>Oct - Nov 2026, 100% virtual</li>
          <li>Free to be discovered</li>
          <li>AI-Interview, 30&ndash;45 min</li>
          <li>Track progress on your dashboard</li>
        </ul>
      </div>
    </section>
  );
}
